import {
  Module,
  ModuleData,
  RHCConfigurationPayload,
  RHCHeatingSourceConfigurationPayload,
  RoomHeatingControllerConfiguration,
  TemperatureControlMode,
} from '@library/store/modules/modules.interface';
import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { User } from '../user/user.interface';
import {
  AckLocalResourcesPayload,
  GetHomeInfoPayload,
  HeatingSystem,
  Home,
  HomeCoachsData,
  HomeData,
  HomeSyncPayload,
  HomesNameUpdate,
  LoadComparisonDataPayload,
  LoadComparisonDataSuccessPayload,
  LoadForecastPayload,
  LoadForecastSuccessPayload,
  Place,
  PlaceUpdate,
  SetHeatingTypeForHeatingControllerPayload,
  SetHeatingTypeForHousePayload,
  SetHomeConfigPayload,
  SetHomeSizePayload,
  SetHomeTypePayload,
  SetStatePayload,
  SetThermPointMode,
  SyncError,
} from './homes.interface';

export const HomesActions = createActionGroup({
  source: 'Homes',
  events: {
    'Get Homes': props<{ payload: any }>(),
    'Get Homes Success': props<{ payload: HomeData[]; modules?: ModuleData[] }>(),
    'Get Homes Failure': props<{ payload: any }>(),

    'Get Heating System': props<{ payload: { homeId: string } }>(),
    'Get Heating System Success': props<{ payload: HeatingSystem }>(),
    'Get Heating System Failure': props<{ payload: any }>(),

    'Get Home Heating Controller': props<{ payload: { gatewayId: string } }>(),
    'Get Home Heating Controller Success': props<{ payload: RoomHeatingControllerConfiguration }>(),
    'Get Home Heating Controller Failure': props<{ payload: any }>(),

    'Select Home': props<{ payload: string }>(),

    'Update Home Place': props<{ payload: PlaceUpdate }>(),
    'Update Home Place Success': props<{ payload: { updatedHome: Place; home_id: string } }>(),
    'Update Home Place Failure': props<{ payload: any }>(),

    'Get Home Status': props<{ payload: GetHomeInfoPayload }>(),
    'Get Home Status Success': props<{ payload: HomeSyncPayload }>(),
    'Get Home Status Failure': props<{ payload: any }>(),

    'Get Readonly Device State': emptyProps(),
    'Get Readonly Device State Success': props<{ modules: Module[]; errors: SyncError[] }>(),
    'Get Readonly Device State Failure': props<{ payload: any }>(),

    'Get Home Config': props<{ payload: GetHomeInfoPayload }>(),
    'Get Home Config Success': props<{ payload: HomeSyncPayload }>(),
    'Get Home Config Failure': props<{ payload: any }>(),

    'Set Home Gateway Config': props<{ payload: SetHomeConfigPayload }>(),
    'Set Home Gateway Config Success': emptyProps(),
    'Set Home Gateway Config Failure': props<{ payload: any }>(),

    'Get Home Users': props<{ payload: string }>(),
    'Get Home Users Success': props<{ payload: { id: string; users: Record<string, User> } }>(),
    'Get Home Users Failure': props<{ payload: any }>(),
    'Remove User From Home': props<{ payload: { homeId: string; userId: string } }>(),
    'Remove User From Home Success': props<{ payload: { homeId: string; userId: string } }>(),
    'Remove User From Home Failure': props<{ payload: any }>(),

    'Update Home Name': props<{ payload: HomesNameUpdate }>(),
    'Update Home Name Success': props<{ payload: HomesNameUpdate }>(),
    'Update Home Name Failure': props<{ payload: any }>(),

    'Get Admin Access Code': props<{ payload: string }>(),
    'Get Admin Access Code Success': props<{ payload: { homeId: string; accessCode: string } }>(),
    'Get Admin Access Code Failure': props<{ payload: any }>(),

    'Set Home State': props<{ payload: SetStatePayload; header?: Record<string, string> }>(),
    'Set Home State Success': emptyProps(),
    'Set Home State Error': props<{ payload: { errors: SyncError[] } }>(),
    'Set Home State Failure': props<{ payload: any }>(),

    'Show Spinner': emptyProps(),
    'Hide Spinner': emptyProps(),
    'Show Error Modal': emptyProps(),
    'Hide Error Modal': emptyProps(),

    'Set Temperature Control Mode': props<{ payload: { home: Partial<Home> } }>(),
    'Set Temperature Control Success': props<{ payload: { home: Partial<Home> } }>(),
    'Set Temperature Control Failure': props<{ payload: any }>(),

    'Successfully Retrieved Home Address': props<{ payload: { address: string; home_id: string } }>(),
    'Failed To Retrieve Home Address': props<{ payload: any }>(),

    'Get Home NFA': props<{ payload: { home_id: string; app_type: string } }>(),
    'Get Home NFA Success': props<{ payload: any }>(),
    'Get Home NFA Failure': props<{ payload: any }>(),

    'Remove Room From Home': props<{ payload: { home_id: string; room_id: string } }>(),
    'Remove Device From Home': props<{ payload: { home_id: string; device_id: string } }>(),
    'Remove Device From Home Success': props<{ payload: { home_id: string; device_id: string } }>(),
    'Remove Device From Home Failure': props<{ payload: any }>(),

    'Set Home Type': props<{ payload: { home: SetHomeTypePayload } }>(),
    'Set Home Type Success': props<{ payload: { home: SetHomeTypePayload } }>(),
    'Set Home Type Failure': props<{ payload: any }>(),

    'Set Therm Mode': props<{ homeId: string; therm_mode: string; endtime?: number }>(),
    'Set Therm Mode Success': props<{ homeId: string; therm_mode: string; endtime?: number }>(),
    'Set Therm Mode Failure': props<{ error: any }>(),

    'Set Therm Point': props<{
      params: { home_id: string; room_id: string; mode: SetThermPointMode; endtime: number | null; temp: number };
    }>(),
    'Set Therm Point Success': props<{
      home_id: string;
      room_id: string;
      mode: SetThermPointMode;
      endtime: number | null;
      temp: number;
    }>(),
    'Set Therm Point Failure': props<{ error: any }>(),

    'Set Thermoregulation Heating Mode': props<{
      payload: {
        home: {
          id: string;
          temperature_control_mode: TemperatureControlMode;
          therm_mode: string;
          therm_mode_endtime?: number;
        };
      };
    }>(),
    'Set Thermoregulation Heating Mode Success': props<{
      payload: {
        home: {
          id: string;
          temperature_control_mode: TemperatureControlMode;
          therm_mode: string;
          therm_mode_endtime?: number;
        };
      };
    }>(),
    'Set Thermoregulation Heating Mode Failure': props<{ error: any }>(),

    'Set Thermoregulation Cooling Mode': props<{
      payload: {
        home: {
          id: string;
          temperature_control_mode: TemperatureControlMode;
          cooling_mode: string;
          cooling_mode_endtime?: number;
        };
      };
    }>(),
    'Set Thermoregulation Cooling Mode Success': props<{
      payload: {
        home: {
          id: string;
          temperature_control_mode: TemperatureControlMode;
          cooling_mode: string;
          cooling_mode_endtime?: number;
        };
      };
    }>(),
    'Set Thermoregulation Cooling Mode Failure': props<{ error: any }>(),

    'Set Thermoregulation Auto Mode': props<{
      payload: {
        home: {
          id: string;
          temperature_control_mode: TemperatureControlMode;
          auto_temp_mode: string;
          auto_mode_endtime?: number;
        };
      };
    }>(),
    'Set Thermoregulation Auto Mode Success': props<{
      payload: {
        home: {
          id: string;
          temperature_control_mode: TemperatureControlMode;
          auto_temp_mode: string;
          auto_mode_endtime?: number;
        };
      };
    }>(),
    'Set Thermoregulation Auto Mode Failure': props<{ error: any }>(),

    'Ack Local Resources': props<{ payload: AckLocalResourcesPayload; header?: Record<string, string> }>(),
    'Ack Local Resources Success': emptyProps(),
    'Ack Local Resources Failure': props<{ error: any }>(),

    'Load Forecast': props<{ payload: LoadForecastPayload }>(),
    'Load Forecast Success': props<{ payload: LoadForecastSuccessPayload }>(),
    'Load Forecast Failure': props<{ error: any }>(),

    'Load Reports Comparison': props<{ payload: LoadComparisonDataPayload }>(),
    'Load Reports Comparison Success': props<{ payload: LoadComparisonDataSuccessPayload }>(),
    'Load Reports Comparison Failure': props<{ error: any }>(),

    'Get Home Coachs': props<{ payload: any }>(),
    'Get Home Coachs Success': props<{ payload: HomeCoachsData }>(),
    'Get Home Coachs Failure': props<{ payload: any }>(),

    'Remove Home': props<{ payload: { id: string } }>(),

    'Set Home Heating System': props<{ payload: SetHeatingTypeForHousePayload }>(),
    'Set Home Heating System Success': props<{ payload: any }>(),
    'Set Home Heating System Failure': props<{ payload: any }>(),

    'Set Home Size': props<{ payload: SetHomeSizePayload }>(),
    'Set Home Size Success': props<{ payload: any }>(),
    'Set Home Size Failure': props<{ payload: any }>(),

    'Set Heating Controller Heating System': props<{ payload: SetHeatingTypeForHeatingControllerPayload }>(),
    'Set Heating Controller Heating System Success': props<{ payload: any }>(),
    'Set Heating Controller Heating System Failure': props<{ payload: any }>(),

    // HVAC RHC
    'Set RHC Configuration': props<{ payload: RHCConfigurationPayload }>(),
    'Set RHC Configuration Success': props<{ payload: any }>(),
    'Set RHC Configuration Failure': props<{ payload: any }>(),
    // HVAC Heating Source conf
    'Set RHC Heating Source Configuration': props<{ payload: RHCHeatingSourceConfigurationPayload }>(),
    'Set RHC Heating Source Configuration Success': props<{ payload: any }>(),
    'Set RHC Heating Source Configuration Failure': props<{ payload: any }>(),
  },
});

export type HomesActionsUnion = ReturnType<(typeof HomesActions)[keyof typeof HomesActions]>;

export type SelectHome = ReturnType<typeof HomesActions.selectHome>;

export type UpdateHomePlaceData = ReturnType<typeof HomesActions.updateHomePlace>;
export type UpdateHomePlaceDataSuccess = ReturnType<typeof HomesActions.updateHomePlaceSuccess>;
export type UpdateHomePlaceDataFailure = ReturnType<typeof HomesActions.updateHomePlaceFailure>;

export type GetHomeStatus = ReturnType<typeof HomesActions.getHomeStatus>;
export type GetHomeStatusSuccess = ReturnType<typeof HomesActions.getHomeStatusSuccess>;
export type GetHomeStatusFailure = ReturnType<typeof HomesActions.getHomeStatusFailure>;

export type GetHeatingSystem = ReturnType<typeof HomesActions.getHeatingSystem>;
export type GetHeatingSystemSuccess = ReturnType<typeof HomesActions.getHeatingSystemSuccess>;
export type GetHeatingSystemFailure = ReturnType<typeof HomesActions.getHeatingSystemFailure>;

export type SetRHCConfiguration = ReturnType<typeof HomesActions.setRHCConfiguration>;
export type SetRHCConfigurationSuccess = ReturnType<typeof HomesActions.setRHCConfigurationSuccess>;
export type SetRHCConfigurationFailure = ReturnType<typeof HomesActions.setRHCConfigurationFailure>;

export type SetRHCHeatingSourceConfiguration = ReturnType<typeof HomesActions.setRHCHeatingSourceConfiguration>;
export type SetRHCHeatingSourceConfigurationSuccess = ReturnType<
  typeof HomesActions.setRHCHeatingSourceConfigurationSuccess
>;
export type SetRHCHeatingSourceConfigurationFailure = ReturnType<
  typeof HomesActions.setRHCHeatingSourceConfigurationFailure
>;

export type GetHomeConfig = ReturnType<typeof HomesActions.getHomeConfig>;
export type GetHomeConfigSuccess = ReturnType<typeof HomesActions.getHomeConfigSuccess>;
export type GetHomeConfigFailure = ReturnType<typeof HomesActions.getHomeConfigFailure>;

export type SetHomeGatewayConfig = ReturnType<typeof HomesActions.setHomeGatewayConfig>;
export type SetHomeGatewayConfigFailure = ReturnType<typeof HomesActions.setHomeGatewayConfigFailure>;
export type SetHomeGatewayConfigSuccess = ReturnType<typeof HomesActions.setHomeGatewayConfigSuccess>;

export type GetHomesUsers = ReturnType<typeof HomesActions.getHomeUsers>;
export type GetHomesUsersSuccess = ReturnType<typeof HomesActions.getHomeUsersSuccess>;
export type GetHomesUsersFailure = ReturnType<typeof HomesActions.getHomeUsersFailure>;

export type UpdateHomeName = ReturnType<typeof HomesActions.updateHomeName>;
export type UpdateHomeNameSuccess = ReturnType<typeof HomesActions.updateHomeNameSuccess>;
export type UpdateHomeNameFailure = ReturnType<typeof HomesActions.updateHomeNameFailure>;

export type SetHomeState = ReturnType<typeof HomesActions.setHomeState>;
export type SetHomeStateSuccess = ReturnType<typeof HomesActions.setHomeStateSuccess>;
export type SetHomeStateError = ReturnType<typeof HomesActions.setHomeStateError>;
export type SetHomeStateFailure = ReturnType<typeof HomesActions.setHomeStateFailure>;

export type HomesActionUnion = ReturnType<(typeof HomesActions)[keyof typeof HomesActions]>;

export type GetHomes = ReturnType<typeof HomesActions.getHomes>;
export type GetHomesSuccess = ReturnType<typeof HomesActions.getHomesSuccess>;
export type GetHomesFailure = ReturnType<typeof HomesActions.getHomesFailure>;

export type GetHomeCoachs = ReturnType<typeof HomesActions.getHomeCoachs>;
export type GetHomeCoachsSuccess = ReturnType<typeof HomesActions.getHomeCoachsSuccess>;
export type GetHomeCoachsFailure = ReturnType<typeof HomesActions.getHomeCoachsFailure>;

export type UpdateHomePlace = ReturnType<typeof HomesActions.updateHomePlace>;
export type UpdateHomePlaceSuccess = ReturnType<typeof HomesActions.updateHomePlaceSuccess>;
export type UpdateHomePlaceFailure = ReturnType<typeof HomesActions.updateHomePlaceFailure>;

export type GetReadonlyDeviceState = ReturnType<typeof HomesActions.getReadonlyDeviceState>;
export type GetReadonlyDeviceStateSuccess = ReturnType<typeof HomesActions.getReadonlyDeviceStateSuccess>;
export type GetReadonlyDeviceStateFailure = ReturnType<typeof HomesActions.getReadonlyDeviceStateFailure>;

export type SetConfig = ReturnType<typeof HomesActions.setHomeGatewayConfig>;
export type SetConfigFailure = ReturnType<typeof HomesActions.setHomeGatewayConfigFailure>;
export type SetConfigSuccess = ReturnType<typeof HomesActions.setHomeGatewayConfigSuccess>;

export type GetHomeUsers = ReturnType<typeof HomesActions.getHomeUsers>;
export type GetHomeUsersSuccess = ReturnType<typeof HomesActions.getHomeUsersSuccess>;
export type GetHomeUsersFailure = ReturnType<typeof HomesActions.getHomeUsersFailure>;

export type RemoveUserFromHome = ReturnType<typeof HomesActions.removeUserFromHome>;
export type RemoveUserFromHomeSuccess = ReturnType<typeof HomesActions.removeUserFromHomeSuccess>;
export type RemoveUserFromHomeFailure = ReturnType<typeof HomesActions.removeUserFromHomeFailure>;

export type GetAdminAccessCode = ReturnType<typeof HomesActions.getAdminAccessCode>;
export type GetAdminAccessCodeSuccess = ReturnType<typeof HomesActions.getAdminAccessCodeSuccess>;
export type GetAdminAccessCodeFailure = ReturnType<typeof HomesActions.getAdminAccessCodeFailure>;

export type ShowSpinner = ReturnType<typeof HomesActions.showSpinner>;
export type HideSpinner = ReturnType<typeof HomesActions.hideSpinner>;
export type ShowErrorModal = ReturnType<typeof HomesActions.showErrorModal>;
export type HideErrorModal = ReturnType<typeof HomesActions.hideErrorModal>;

export type SetState = ReturnType<typeof HomesActions.setHomeState>;
export type SetStateSuccess = ReturnType<typeof HomesActions.setHomeStateSuccess>;
export type SetStateErrors = ReturnType<typeof HomesActions.setHomeStateError>;
export type SetStateFailure = ReturnType<typeof HomesActions.setHomeStateFailure>;

export type SetTemperatureControlMode = ReturnType<typeof HomesActions.setTemperatureControlMode>;
export type SetTemperatureControlModeSuccess = ReturnType<typeof HomesActions.setTemperatureControlSuccess>;
export type SetTemperatureControlModeFailure = ReturnType<typeof HomesActions.setTemperatureControlFailure>;

export type GetHomeAddressSuccess = ReturnType<typeof HomesActions.successfullyRetrievedHomeAddress>;
export type GetHomeAddressFailure = ReturnType<typeof HomesActions.failedToRetrieveHomeAddress>;

export type GetHomeNFA = ReturnType<typeof HomesActions.getHomeNFA>;
export type GetHomeNFASuccess = ReturnType<typeof HomesActions.getHomeNFASuccess>;
export type GetHomeNFAFailure = ReturnType<typeof HomesActions.getHomeNFAFailure>;

export type RemoveRoomFromHome = ReturnType<typeof HomesActions.removeRoomFromHome>;

export type SetHomeType = ReturnType<typeof HomesActions.setHomeType>;
export type SetHomeTypeSuccess = ReturnType<typeof HomesActions.setHomeTypeSuccess>;
export type SetHomeTypeFailure = ReturnType<typeof HomesActions.setHomeTypeFailure>;

export type GetHomeHeatingController = ReturnType<typeof HomesActions.getHomeHeatingController>;
export type GetHomeHeatingControllerSuccess = ReturnType<typeof HomesActions.getHomeHeatingControllerSuccess>;
export type GetHomeHeatingControllerFailure = ReturnType<typeof HomesActions.getHomeHeatingControllerFailure>;

export type SetHeatingControllerHeatingSystem = ReturnType<typeof HomesActions.setHeatingControllerHeatingSystem>;
export type SetHeatingControllerHeatingSystemSuccess = ReturnType<
  typeof HomesActions.setHeatingControllerHeatingSystemSuccess
>;
export type SetHeatingControllerHeatingSystemFailure = ReturnType<
  typeof HomesActions.setHeatingControllerHeatingSystemFailure
>;

export type SetHomeSize = ReturnType<typeof HomesActions.setHomeSize>;
export type SetHomeSizeSuccess = ReturnType<typeof HomesActions.setHomeSizeSuccess>;
export type SetHomeSizeFailure = ReturnType<typeof HomesActions.setHomeSizeFailure>;

export type RemoveDeviceFromHome = ReturnType<typeof HomesActions.removeDeviceFromHome>;
export type RemoveDeviceFromHomeSuccess = ReturnType<typeof HomesActions.removeDeviceFromHomeSuccess>;
export type RemoveDeviceFromHomeFailure = ReturnType<typeof HomesActions.removeDeviceFromHomeFailure>;

export type SetThermMode = ReturnType<typeof HomesActions.setThermMode>;
export type SetThermModeSuccess = ReturnType<typeof HomesActions.setThermModeSuccess>;
export type SetThermModeFailure = ReturnType<typeof HomesActions.setThermModeFailure>;

export type SetThermPoint = ReturnType<typeof HomesActions.setThermPoint>;
export type SetThermPointSuccess = ReturnType<typeof HomesActions.setThermPointSuccess>;
export type SetThermPointFailure = ReturnType<typeof HomesActions.setThermPointFailure>;

export type SetHeatingMode = ReturnType<typeof HomesActions.setThermoregulationHeatingMode>;
export type SetHeatingModeSuccess = ReturnType<typeof HomesActions.setThermoregulationHeatingModeSuccess>;
export type SetHeatingModeFailure = ReturnType<typeof HomesActions.setThermoregulationHeatingModeFailure>;

export type SetCoolingMode = ReturnType<typeof HomesActions.setThermoregulationCoolingMode>;
export type SetCoolingModeSuccess = ReturnType<typeof HomesActions.setThermoregulationCoolingModeSuccess>;
export type SetCoolingModeFailure = ReturnType<typeof HomesActions.setThermoregulationCoolingModeFailure>;

export type SetAutoMode = ReturnType<typeof HomesActions.setThermoregulationAutoMode>;
export type SetAutoModeSuccess = ReturnType<typeof HomesActions.setThermoregulationAutoModeSuccess>;
export type SetAutoModeFailure = ReturnType<typeof HomesActions.setThermoregulationAutoModeFailure>;

export type AckLocalResources = ReturnType<typeof HomesActions.ackLocalResources>;
export type AckLocalResourcesSuccess = ReturnType<typeof HomesActions.ackLocalResourcesSuccess>;
export type AckLocalResourcesFailure = ReturnType<typeof HomesActions.ackLocalResourcesFailure>;

export type LoadForecast = ReturnType<typeof HomesActions.loadForecast>;
export type LoadForecastSuccess = ReturnType<typeof HomesActions.loadForecastSuccess>;
export type LoadForecastFailure = ReturnType<typeof HomesActions.loadForecastFailure>;

export type LoadComparisonData = ReturnType<typeof HomesActions.loadReportsComparison>;
export type LoadComparisonDataSuccess = ReturnType<typeof HomesActions.loadReportsComparisonSuccess>;
export type LoadComparisonDataFailure = ReturnType<typeof HomesActions.loadReportsComparisonFailure>;

export type RemoveHome = ReturnType<typeof HomesActions.removeHome>;
